import React, { useMemo } from 'react';
import { HexColorPicker } from 'react-colorful';
import { useForm, useFormState } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { FieldGroup } from '@xsolla/ui-core';
import { ImageUploader, UploaderValue } from '@xsolla/ui-uploader';

import { uploadMerchantFile } from '../../../api';
import { OAuthConnectionFieldsType } from '../../../api/configurations/oauth2connection/types';
import { DEFAULT_CUSTOM_OAUTH2_COLOR } from '../../../constants';
import { useConfig } from '../../../contexts/config';
import { validateHexColor } from '../../../utils';
import { FieldBlock, FieldInput } from '../../ui';
import { CustomizationColorInputWrapper as ColorInputWrapper, HiddenWrapper } from './style';
import { formatUploadLink } from './utils';
import { DISPLAY_NAME_MAX_LENGTH, IMAGE_MAX_FILE_SIZE } from './validator';

const CustomizationFields = (): JSX.Element => {
  const intl = useIntl();
  const {
    mutators: { changeBackgroundColor, changeLogoUrl }
  } = useForm<OAuthConnectionFieldsType>();
  const {
    errors,
    values: { logo_url, background_color },
    modified,
    submitFailed
  } = useFormState<OAuthConnectionFieldsType>();
  const { merchantId } = useConfig();

  const image = useMemo(
    () =>
      logo_url
        ? {
            filename: '',
            url: logo_url
          }
        : null,
    [logo_url]
  );

  const uploadLogo = async (file: File) => {
    // Check if image is bigger than 300kb
    if (file.size > IMAGE_MAX_FILE_SIZE) {
      return new Promise<UploaderValue>((_, reject) => {
        reject(
          new Error(
            intl.formatMessage({
              id: 'app.validation.logo_url.size-error'
            })
          )
        );
      });
    }

    const formData = new FormData();
    formData.append('file', file);
    const response = await uploadMerchantFile(merchantId, formData);

    return new Promise<UploaderValue>((resolve, reject) => {
      if (response.file_url) {
        resolve({ filename: '', url: response.file_url });
      }
      reject(
        new Error(
          intl.formatMessage({
            id: 'app.validation.logo_url.upload-error'
          })
        )
      );
    });
  };

  const deleteLogo = () =>
    new Promise<void>((resolve) => {
      changeLogoUrl('');
      resolve();
    });

  return (
    <FieldBlock
      title={intl.formatMessage({ id: 'enterprise-connection.settings.customization.title' })}
      description={intl.formatMessage({
        id: 'enterprise-connection.settings.customization.subtitle'
      })}>
      <>
        <FieldGroup
          size="sm"
          label={intl.formatMessage({
            id: 'enterprise-connection.settings.customization.display_name.label'
          })}
          tooltip={intl.formatMessage({
            id: 'enterprise-connection.settings.customization.display_name.tooltip'
          })}>
          <FieldInput
            name="display_name"
            data-test-id="oauth2_field_display_name"
            errorMessage={
              errors &&
              errors.display_name &&
              intl.formatMessage({ id: errors.display_name }, { number: DISPLAY_NAME_MAX_LENGTH })
            }
          />
        </FieldGroup>

        <FieldGroup
          label={intl.formatMessage({
            id: 'enterprise-connection.settings.customization.logo_url.label'
          })}
          footer={
            // eslint-disable-next-line react/jsx-wrap-multilines
            <FormattedMessage
              id="enterprise-connection.settings.customization.logo_url.footer"
              values={{
                newLine: <br />
              }}
            />
          }
          size="md">
          <ImageUploader
            data-test-id="oauth2_field_logo_url"
            errorMessage={
              (submitFailed || (modified && modified.logo_url)) &&
              errors &&
              errors.logo_url &&
              intl.formatMessage({ id: errors.logo_url })
            }
            onChange={(file) => {
              if (file && file.url) {
                changeLogoUrl(formatUploadLink(file.url));
              }
            }}
            options={{
              uploadFn: uploadLogo,
              deleteFn: deleteLogo
            }}
            size="md"
            value={image}
          />

          <HiddenWrapper>
            {/* Hidden input for handling final form touched param */}
            <FieldInput name="logo_url" />
          </HiddenWrapper>
        </FieldGroup>

        <FieldGroup
          label={intl.formatMessage({
            id: 'enterprise-connection.settings.customization.background_color.label'
          })}
          size="md">
          <HexColorPicker
            color={
              validateHexColor(background_color) ? background_color : DEFAULT_CUSTOM_OAUTH2_COLOR
            }
            onChange={changeBackgroundColor}
          />

          <ColorInputWrapper>
            <FieldInput
              name="background_color"
              data-test-id="oauth2_field_background_color"
              errorMessage={
                errors &&
                errors.background_color &&
                intl.formatMessage({ id: errors.background_color })
              }
            />
          </ColorInputWrapper>
        </FieldGroup>
      </>
    </FieldBlock>
  );
};

export default CustomizationFields;
